/**
 * Timed camera moves for the capture lane (q-capture-camera-channel-devcam-bridge;
 * design docs/social-media/studio-scene-and-verb-design-2026-09-18.md §4).
 *
 * The studio's in-shot schedule could only hold or press a key, so a pan, a
 * dolly or a tilt had nowhere to be said. `window.__GC_DEVCAM` (devBridge.ts,
 * registered by App in dev builds only) takes ONE op shape — a timed ease of the
 * orbit boom — and this module is its whole logic: parse the op, then give the
 * pose at any elapsed time. Pure, so the Node harness can test it without a DOM.
 *
 * Bounded by CAMERA_BOUNDS (camera.ts). A target outside the envelope is REFUSED,
 * not clamped: a silently clamped shot films something the plan did not ask for,
 * and a refusal is a finding the studio can report. Every pose on the way is
 * inside the envelope too — the ease runs between two in-bounds values.
 *
 * Deliberately absent: a look-at-a-world-point op. It needs world coordinates
 * the plan does not carry today; add it when a shot needs it, not before.
 * Presentation-only: nothing here rides the wire or touches the sim.
 */

import { CAMERA_BOUNDS } from './camera.js';

/** The boom pose a move eases (same fields as a capture preset). */
export interface BoomPose {
  readonly yaw: number;
  readonly pitch: number;
  readonly dist: number;
}

/** A validated move: yaw is relative (a pan), pitch/dist are absolute targets or unchanged. */
export interface CameraMove {
  readonly yawBy: number;
  readonly pitchTo: number | null;
  readonly distTo: number | null;
  readonly seconds: number;
}

/** Longest accepted move — a shot, not a cutscene. */
export const CAMERA_MOVE_MAX_SECONDS = 30;
/** Largest accepted pan either way: one full revolution. */
export const CAMERA_MOVE_MAX_YAW = 2 * Math.PI;

const OP_KEYS: ReadonlySet<string> = new Set(['yawBy', 'pitchTo', 'distTo', 'seconds']);

function finite(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Validate a bridge op. Returns null — never throws — for anything that is not a
 * move this module can keep inside CAMERA_BOUNDS: a non-object, an unknown key
 * (a typo such as `lookAt` is a finding, not a silent no-op), a non-finite or
 * out-of-range number, a missing `seconds`, or an op that moves nothing.
 */
export function parseCameraMove(op: unknown): CameraMove | null {
  if (typeof op !== 'object' || op === null || Array.isArray(op)) return null;
  const rec = op as Record<string, unknown>;
  if (Object.keys(rec).some((k) => !OP_KEYS.has(k))) return null;

  const { seconds, yawBy, pitchTo, distTo } = rec;
  if (!finite(seconds) || seconds < 0 || seconds > CAMERA_MOVE_MAX_SECONDS) return null;
  if (yawBy !== undefined && (!finite(yawBy) || Math.abs(yawBy) > CAMERA_MOVE_MAX_YAW)) {
    return null;
  }
  if (
    pitchTo !== undefined &&
    (!finite(pitchTo) || pitchTo < CAMERA_BOUNDS.MIN_PITCH || pitchTo > CAMERA_BOUNDS.MAX_PITCH)
  ) {
    return null;
  }
  if (
    distTo !== undefined &&
    (!finite(distTo) || distTo < CAMERA_BOUNDS.MIN_DIST || distTo > CAMERA_BOUNDS.MAX_DIST)
  ) {
    return null;
  }
  if (yawBy === undefined && pitchTo === undefined && distTo === undefined) return null;

  return {
    yawBy: yawBy ?? 0,
    pitchTo: pitchTo ?? null,
    distTo: distTo ?? null,
    seconds,
  };
}

/** Smoothstep: zero velocity at both ends, so a pan starts and settles without a jolt. */
function ease(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * The boom pose `elapsedSec` into `move`, starting from `from`. Before the start
 * it is `from`; at or past `seconds` it is exactly the target (a 0 s move is a
 * cut). Pitch/dist are interpolated between `from` and an in-bounds target, and
 * `from` is clamped first, so the result is inside CAMERA_BOUNDS for every input.
 */
export function cameraMovePose(from: BoomPose, move: CameraMove, elapsedSec: number): BoomPose {
  const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
  const start = {
    yaw: from.yaw,
    pitch: clamp(from.pitch, CAMERA_BOUNDS.MIN_PITCH, CAMERA_BOUNDS.MAX_PITCH),
    dist: clamp(from.dist, CAMERA_BOUNDS.MIN_DIST, CAMERA_BOUNDS.MAX_DIST),
  };
  const raw = move.seconds <= 0 ? 1 : (Number.isFinite(elapsedSec) ? elapsedSec : 0) / move.seconds;
  const k = ease(clamp(raw, 0, 1));
  const pitchTo = move.pitchTo ?? start.pitch;
  const distTo = move.distTo ?? start.dist;
  // `a + (b - a) * 1` is not always `b` in floating point (1.45 → 0.15 lands on
  // 0.1499…, found by the containment sweep), so the lerp is clamped as well:
  // an edge target must not end a hair outside the envelope it was checked against.
  return {
    yaw: start.yaw + move.yawBy * k,
    pitch: clamp(
      start.pitch + (pitchTo - start.pitch) * k,
      CAMERA_BOUNDS.MIN_PITCH,
      CAMERA_BOUNDS.MAX_PITCH,
    ),
    dist: clamp(
      start.dist + (distTo - start.dist) * k,
      CAMERA_BOUNDS.MIN_DIST,
      CAMERA_BOUNDS.MAX_DIST,
    ),
  };
}

/** Whether the move has reached its target at `elapsedSec`. */
export function cameraMoveDone(move: CameraMove, elapsedSec: number): boolean {
  return !(elapsedSec < move.seconds);
}
